import {
  ZRM_MULTIPLEX_LISTING_FLOW_ENABLED,
  ZRM_TOTAL_MONTHLY_PRICE_ENABLED,
  ZRM_USER_ONBOARDING_ENABLED,
} from '../constants/FeatureFlagConstants';

import { USER_ONBOARDING_DISMISSED_KEY } from '../constants/UserOnboardingConstants';

import { getCookieAsBoolean } from './cookieUtils';
import propertyUtils from './propertyUtils';

import type {
  GetUserPropertyDetailsListingTypeWebDto,
  RentalManagerAuthCheckUserInfoWebApiDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

const canAccessModularListing = ({
  isMultifamily,
  listingTypeCode,
  lastActivatedMs,
  userCanAccessModularListing,
}: {
  isMultifamily: boolean;
  listingTypeCode: number;
  lastActivatedMs: number | undefined;
  userCanAccessModularListing: boolean;
}): boolean => {
  const hasListingBeenActivated = Boolean(lastActivatedMs);
  const isSingleFamilyRental = !isMultifamily && listingTypeCode === 1;
  const isRoomForRent = listingTypeCode === 5;

  return !hasListingBeenActivated && (isSingleFamilyRental || isRoomForRent) && userCanAccessModularListing;
};

const canAccessTopOfFunnelPremium = ({
  isFreemiumEnabled,
  isTopOfFunnelEnrollmentEligible,
  isActiveProductTypePremium,
}: {
  isFreemiumEnabled: boolean;
  isTopOfFunnelEnrollmentEligible: boolean;
  isActiveProductTypePremium: boolean;
}): boolean => {
  return isFreemiumEnabled && (isActiveProductTypePremium || isTopOfFunnelEnrollmentEligible);
};

const canAccessMLReactivationFlow = ({
  lastActivatedMs,
  isFeed,
  isMultifamily,
}: {
  lastActivatedMs?: number;
  isFeed?: boolean;
  isMultifamily?: boolean;
}) => {
  return Boolean((lastActivatedMs || isFeed) && !isMultifamily);
};

const canAccessSFCostsAndFees = ({ isFeed, isMultifamily }: { isFeed: boolean; isMultifamily: boolean }) =>
  !isFeed && !isMultifamily;

const canAccessFastPost = ({
  isMultifamily,
  lastActivatedMs,
  isFeed,
  restrictions,
}: {
  isMultifamily: boolean;
  lastActivatedMs: number | undefined;
  isFeed: boolean;
  restrictions: { applications: { isAccessRestricted: boolean } };
}) => {
  return !isMultifamily && !lastActivatedMs && !isFeed && !restrictions?.applications?.isAccessRestricted;
};

const canAccessPostingPathTourScheduling = ({
  isFeed,
  isInReactivationFlow,
  canAccessRelisting,
  active,
}: {
  isFeed: boolean;
  isInReactivationFlow: boolean;
  canAccessRelisting: boolean;
  active: boolean;
}) => {
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessRelistingFlow = ({ canBeRelisted }: { canBeRelisted: boolean }) => {
  return canBeRelisted;
};

const canAccessListingPreview = ({
  active,
  isFeed,
  isMultifamily,
  listingType,
}: Partial<{
  active: boolean;
  isFeed: boolean;
  isMultifamily: boolean;
  listingType: GetUserPropertyDetailsListingTypeWebDto;
}>) => {
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: { listingType } });

  return !active && !isRoomForRent && !isFeed && !isMultifamily;
};

const canAccessTotalMonthlyPriceWithFee = ({ isEligible = false }: { isEligible?: boolean }) =>
  getCookieAsBoolean(ZRM_TOTAL_MONTHLY_PRICE_ENABLED) || isEligible;

const isTourSchedulingEnabledForProperty = ({
  active,
  canBeRelisted,
  isFeed,
  isInReactivationFlow,
}: {
  active: boolean;
  canBeRelisted: boolean;
  isFeed: boolean;
  isInReactivationFlow: boolean;
}) => {
  const canAccessRelisting = canAccessRelistingFlow({ canBeRelisted });
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessUserOnboardingExistingUser = ({ user }: { user: RentalManagerAuthCheckUserInfoWebApiDto | null }) => {
  if (!user) return false;

  if (!user?.restrictions?.isAllowed) {
    return false;
  }

  if (typeof window !== 'undefined' && sessionStorage.getItem(USER_ONBOARDING_DISMISSED_KEY) === 'true') {
    return false;
  }

  const hasListings = (user?.summary?.totalListingCount ?? 0) > 0;

  if (getCookieAsBoolean(ZRM_USER_ONBOARDING_ENABLED)) {
    return hasListings;
  }

  return Boolean(
    hasListings &&
    (!user.firstName ||
      !user.lastName ||
      !user.profiles?.profileType ||
      !user.profiles?.propertyCountRange ||
      !user?.firstName?.trim() ||
      !user?.lastName?.trim()),
  );
};

const canAccessUserOnboardingNewUser = ({
  user,
}: {
  user: {
    restrictions?: { isAllowed: boolean };
    features: { canViewOnboardingFlow: boolean };
  } | null;
}) => {
  if (user?.restrictions?.isAllowed && user.features.canViewOnboardingFlow) {
    return true;
  }

  return false;
};

const canAccessMultiplexListingFlow = ({
  canAccessMultiplexListingFlowResponse,
}: {
  canAccessMultiplexListingFlowResponse: string;
}) => {
  const areRequiredConditionsSatisfied = Boolean(canAccessMultiplexListingFlowResponse === 'true');
  const areOverrideConditionsSatisfied = getCookieAsBoolean(ZRM_MULTIPLEX_LISTING_FLOW_ENABLED);

  return areRequiredConditionsSatisfied || areOverrideConditionsSatisfied;
};

const shouldShowBrokerFeeAlert = ({ state }: { state: string }) => {
  return state ? state === 'MA' : false;
};

export default {
  canAccessModularListing,
  canAccessTopOfFunnelPremium,
  canAccessMLReactivationFlow,
  canAccessSFCostsAndFees,
  canAccessFastPost,
  canAccessPostingPathTourScheduling,
  canAccessTotalMonthlyPriceWithFee,
  canAccessMultiplexListingFlow,
  canAccessRelistingFlow,
  canAccessListingPreview,
  canAccessUserOnboardingNewUser,
  canAccessUserOnboardingExistingUser,
  isTourSchedulingEnabledForProperty,
  shouldShowBrokerFeeAlert,
};
